/**
 * useEscapeKey — Invoke a handler when the user presses Escape.
 *
 * Attaches a `keydown` listener to `window` and dispatches on `Escape`
 * (or `Esc` for older WebKit). Ignores key events whose default has
 * already been handled. Pass `enabled=false` to disable without
 * unmounting the component.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { useEffect } from "react";
import { useStableCallback } from "./useStableCallback.js";

export function useEscapeKey(
  handler: () => void,
  options: { readonly enabled?: boolean } = {},
): void {
  const { enabled = true } = options;
  const stableHandler = useStableCallback(handler);
  useEffect(() => {
    if (!enabled) return undefined;
    const listener = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.key !== "Escape" && event.key !== "Esc") return;
      stableHandler();
    };
    window.addEventListener("keydown", listener);
    return (): void => {
      window.removeEventListener("keydown", listener);
    };
  }, [enabled, stableHandler]);
}
